import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  ChevronDownIcon,
  CheckIcon,
  SignalIcon,
  SignalSlashIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../hooks/useAuth'
import { useAppContext } from '../../context/AppContext'
import { useTheme } from '../../context/ThemeContext'
import { useGitService } from '../../services/git'
import type { Branch } from '../../types/git.types'

// ── TopBar ────────────────────────────────────────────────────────────────────

interface TopBarProps {
  /** Passed from AppShell so TopBar knows sidebar width for layout */
  sidebarCollapsed: boolean
}

export function TopBar({ sidebarCollapsed: _ }: TopBarProps): JSX.Element {
  const { account } = useAuth()
  const { selectedRepo, selectedBranch, setSelectedBranch } = useAppContext()

  const displayName   = account?.name ?? account?.username ?? 'User'
  const initial       = displayName.charAt(0).toUpperCase()
  const isPrivate     = selectedRepo?.visibility === 'private'
  const repoFullName  = selectedRepo?.fullName ?? null

  return (
    <header
      data-testid="topbar"
      className="h-14 bg-surface border-b border-border flex items-center px-4 gap-4 shrink-0"
    >
      {/* ── Left: repo name + visibility badge ─────────────────────────────── */}
      <div className="flex items-center gap-2 min-w-0">
        {repoFullName ? (
          <>
            <span className="text-fg font-medium text-sm truncate">{repoFullName}</span>
            <span
              className={clsx(
                'text-xs px-1.5 py-0.5 rounded border font-medium shrink-0',
                isPrivate
                  ? 'text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:bg-amber-900/30'
                  : 'text-accent  border-accent-line  bg-accent-soft  dark:text-accent  dark:border-accent-line  dark:bg-accent/30',
              )}
            >
              {isPrivate ? 'Private' : 'Public'}
            </span>
          </>
        ) : (
          <span className="text-fg-muted text-sm">No repo selected</span>
        )}
      </div>

      {/* ── Spacer ──────────────────────────────────────────────────────────── */}
      <div className="flex-1" />

      {/* ── Center: branch selector ─────────────────────────────────────────── */}
      <BranchPicker
        repo={selectedRepo}
        selectedBranch={selectedBranch}
        onSelect={setSelectedBranch}
      />

      {/* ── Spacer ──────────────────────────────────────────────────────────── */}
      <div className="flex-1" />

      {/* ── Right: theme toggle + connection status + user avatar ───────────── */}
      <div className="flex items-center gap-3 shrink-0">
        <ThemeToggle />

        {/* Connection indicator — green = live socket, gray = polling */}
        <span
          data-testid="connection-status"
          title="Connected"
          className="text-green-500"
        >
          <SignalIcon className="h-5 w-5" aria-hidden="true" />
        </span>

        {/* User avatar */}
        <div
          className="h-7 w-7 rounded-full bg-accent text-accent-fg flex items-center justify-center text-xs font-medium shrink-0"
          aria-label={displayName}
          title={displayName}
        >
          {initial}
        </div>
      </div>
    </header>
  )
}

// ── ThemeToggle ───────────────────────────────────────────────────────────────

function ThemeToggle(): JSX.Element {
  const { theme, toggleTheme } = useTheme()
  const isDark = theme === 'dark'
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="theme-toggle"
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="p-1.5 rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
    >
      {isDark
        ? <SunIcon className="h-5 w-5" aria-hidden="true" />
        : <MoonIcon className="h-5 w-5" aria-hidden="true" />}
    </button>
  )
}

// ── BranchPicker ──────────────────────────────────────────────────────────────
//
// Lists the active repo's branches (via IGitService.listBranches) and lets the
// operator set the active branch.  Disabled when no repo is selected.  Branches
// load lazily the first time the menu is opened.

interface BranchPickerProps {
  repo:           import('../../types/git.types').Repo | null
  selectedBranch: string
  onSelect:       (branch: string) => void
}

function BranchPicker({ repo, selectedBranch, onSelect }: BranchPickerProps): JSX.Element {
  const git = useGitService()
  const [open, setOpen] = useState(false)
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const disabled = repo === null

  // Load branches when the menu opens (and a repo is selected).
  useEffect(() => {
    if (!open || repo === null) return
    let cancelled = false
    setLoading(true)
    git.listBranches({ owner: repo.owner, repo: repo.name })
      .then((bs) => { if (!cancelled) setBranches(bs) })
      .catch(() => { if (!cancelled) setBranches([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, repo, git])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div className="relative" ref={containerRef}>
      <button
        aria-label={`Current branch: ${selectedBranch}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid="branch-picker"
        className={clsx(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border',
          'text-sm text-fg-muted bg-surface-2 hover:bg-surface-3 transition-colors',
          disabled && 'opacity-40 pointer-events-none',
        )}
      >
        <span className="font-mono truncate max-w-[160px]">{selectedBranch}</span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-fg-muted" />
      </button>

      {open && !disabled && (
        <div
          role="menu"
          data-testid="branch-picker-menu"
          className="absolute z-20 mt-1 left-0 min-w-[14rem] max-h-80 overflow-y-auto bg-surface border border-border rounded-md shadow-lg py-1"
        >
          {loading ? (
            <p className="px-3 py-2 text-xs text-fg-muted">Loading branches…</p>
          ) : branches.length === 0 ? (
            <p className="px-3 py-2 text-xs text-fg-muted">No branches</p>
          ) : (
            branches.map((b) => {
              const isActive = b.name === selectedBranch
              return (
                <button
                  key={b.name}
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    onSelect(b.name)
                    setOpen(false)
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                    isActive ? 'text-fg bg-surface-2' : 'text-fg-muted hover:bg-surface-2',
                  )}
                >
                  <span className="font-mono truncate flex-1">{b.name}</span>
                  {isActive && <CheckIcon className="h-4 w-4 shrink-0 text-accent" />}
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

// Export SignalSlashIcon usage so tree-shaker keeps it for future toggling
export { SignalSlashIcon }
